import React from "react";

import { useFormikContext } from "formik";

import { CheckboxWithUnorderedList } from "shared/components/Form";
import Icon from "shared/components/Icon";
import Settings from "shared/components/Settings";
import Tooltip from "shared/components/Tooltip";
import { RegularText, WeakText } from "shared/components/Typography";
import Well from "shared/components/Well";
import { usePermissions } from "shared/hooks";
import { SurveyModel } from "shared/interfaces/api";
import { conditionalItem } from "shared/utils/array";

import ApplicationSetupHeader from "../Header";
import ResidenceEmploymentDuration, {
  DEFAULT_DURATION_TYPE,
  DEFAULT_DURATION_VALUE,
} from "../ResidenceEmploymentDuration";
import SentilinkIdentificationToggle from "../SentilinkIdentificationToggle";
import { LabelIconContainer, StyledSkeleton as Skeleton } from "../styled";
import {
  getAllowManagersToViewSSNItem,
  getAssetsVerificationItem,
  getBlockApplicationSubmitIfNoIncomeItem,
  getConditionallyProceedItem,
  getForceIncomeDocumentsItem,
  useGuardedForceDocsToggle,
  useGuardedIncomeToggle,
  useHasIncomeDocsSurfaceByPersona,
} from "../utils";

import {
  applicantPersonaIdentification,
  applicantSentilinkIdentification,
  applicantArgyleVerification,
  renterProfile,
  renterProfileLeasePreferences,
  renterProfileReferences,
  incomeAndAssetVerification,
  assetVerificationWhenLinkedEmployment,
  blockApplicationSubmitIfNoIncome,
  forceApplicantIncomeDocuments,
  forceOccupantIncomeDocuments,
  backgroundCreditCheck,
  allowManagerViewSSN,
  residentialHistoryDurationFlag,
  residentialHistoryDurationType,
  residentialHistoryDurationValue,
  employmentHistoryDurationFlag,
  employmentHistoryDurationType,
  employmentHistoryDurationValue,
  usesConditionallyProceed,
  verificationOfResidency,
} from "./constants";
import VorQuestionsTable from "./VorQuestionsTable";

interface ScreeningForApplicantsAndOccupantsProps {
  isArgyleEnabled: boolean;
  isPersonaEnabled: boolean;
  isVerificationOfResidencyEnabled: boolean;
  isConditionallyProceedEnabled: boolean;
  loading?: boolean;
  disabled?: boolean;
  isBgccEnabled: boolean;
  isSurveyLoading: boolean;
  vorSurvey: SurveyModel;
  forSettingsLabel: string;
  toggleVor: Function;
  isVeroGoActive?: boolean;
  isV1ExpressionEngine?: boolean;
}

const ScreeningForApplicantsAndOccupants = ({
  isArgyleEnabled,
  isPersonaEnabled,
  isConditionallyProceedEnabled,
  isVerificationOfResidencyEnabled,
  loading,
  disabled,
  isBgccEnabled,
  isSurveyLoading,
  vorSurvey,
  forSettingsLabel,
  toggleVor,
  isVeroGoActive,
  isV1ExpressionEngine,
}: ScreeningForApplicantsAndOccupantsProps) => {
  const { values, setFieldValue, setValues } = useFormikContext();
  const { isIdFraudRiskEnabled: isSentilinkEnabled } = usePermissions();

  // Disable BGCC section if company is not entitled
  const bgccDisabled = disabled || !isBgccEnabled;

  const assetsVerificationItem = getAssetsVerificationItem(
    isArgyleEnabled,
    values,
    assetVerificationWhenLinkedEmployment,
    disabled
  );

  const blockApplicationSubmitIfNoIncomeItem =
    getBlockApplicationSubmitIfNoIncomeItem(
      values,
      blockApplicationSubmitIfNoIncome
    );

  const incomeDocsByPersona = useHasIncomeDocsSurfaceByPersona();
  const hasDocSurface =
    incomeDocsByPersona.applicant || incomeDocsByPersona.occupant;
  const forceIncomeDocumentsItem = getForceIncomeDocumentsItem({
    values,
    prop: forceApplicantIncomeDocuments,
    isGuarantor: false,
    disabled,
  });
  const onGuardedForceDocsToggle = useGuardedForceDocsToggle({ hasDocSurface });

  const linkEmploymentOn = Boolean(values?.[applicantArgyleVerification]);
  const incomeAndBalancesOn = Boolean(values?.[incomeAndAssetVerification]);
  const showForceDocsInIncomeAndBalances =
    Boolean(isV1ExpressionEngine) && incomeAndBalancesOn;
  const showForceDocsInLinkEmployment =
    Boolean(isV1ExpressionEngine) && linkEmploymentOn && !incomeAndBalancesOn;
  const forceDocsChildItem = {
    label: "Require Document Uploads",
    id: "force-income-documents-applicant-occupant",
    disabled,
    children: [forceIncomeDocumentsItem],
  };
  // Guard toggles: turning off the LAST income surface (Argyle or Plaid) is
  // blocked while docs are still configured — symmetric with the doc-side
  // IncomeSurfaceGate. Both rules are frontend-only.
  const onToggleArgyle = useGuardedIncomeToggle({
    toggleField: applicantArgyleVerification,
    siblingIncomeField: incomeAndAssetVerification,
    personaScope: "applicant",
  });
  const onTogglePlaid = useGuardedIncomeToggle({
    toggleField: incomeAndAssetVerification,
    siblingIncomeField: applicantArgyleVerification,
    personaScope: "applicant",
  });

  return (
    <Settings
      header={
        <Skeleton loading={loading}>
          <ApplicationSetupHeader
            title="Screening For Applicants & Occupants"
            subTitle="Please select the screening level you would like to require from applicants."
            description="over 18 years old"
          />
        </Skeleton>
      }
      nodes={[
        ...conditionalItem(isSentilinkEnabled, {
          key: 5,
          component: (
            <SentilinkIdentificationToggle
              id="applicant-sentilink-identification"
              field={applicantSentilinkIdentification}
              loading={loading}
              disabled={disabled}
            />
          ),
        }),
        ...conditionalItem(isPersonaEnabled, {
          key: 10,
          component: (
            <Skeleton loading={loading}>
              <CheckboxWithUnorderedList
                id="applicant-persona-identification"
                label={
                  isVeroGoActive ? (
                    <LabelIconContainer alignItems="center">
                      <RegularText>Identity Verification</RegularText>
                      <Tooltip
                        title={
                          <Well noBorder>
                            This configuration is required for Vero Go.
                          </Well>
                        }
                      >
                        <Icon.InfoIcon className="extra-info" />
                      </Tooltip>
                    </LabelIconContainer>
                  ) : (
                    "Identity Verification"
                  )
                }
                onChange={(checked) => {
                  setFieldValue(applicantPersonaIdentification, checked);
                }}
                value={values[applicantPersonaIdentification]}
                disabled={disabled || isVeroGoActive}
              />
            </Skeleton>
          ),
        }),
        ...conditionalItem(isArgyleEnabled && !isVeroGoActive, {
          key: 15,
          component: (
            <Skeleton loading={loading}>
              <CheckboxWithUnorderedList
                id="applicant-link-employment"
                label="Link Employment"
                onChange={(checked) => {
                  const applied = onToggleArgyle(checked);
                  if (applied && checked === false && !incomeAndBalancesOn) {
                    setFieldValue(forceApplicantIncomeDocuments, false);
                    setFieldValue(forceOccupantIncomeDocuments, false);
                  }
                }}
                value={values[applicantArgyleVerification]}
                items={[
                  ...conditionalItem(
                    showForceDocsInLinkEmployment,
                    forceDocsChildItem
                  ),
                ]}
                onItemChildChange={(id, value) => {
                  if (id === forceApplicantIncomeDocuments) {
                    const applied = onGuardedForceDocsToggle(
                      forceApplicantIncomeDocuments,
                      value
                    );
                    if (applied) {
                      setFieldValue(forceOccupantIncomeDocuments, value);
                    }
                    return;
                  }
                  setFieldValue(id, value);
                }}
                disabled={disabled}
              />
            </Skeleton>
          ),
        }),
        ...conditionalItem(!isVeroGoActive, {
          key: 20,
          component: (
            <Skeleton loading={loading}>
              <CheckboxWithUnorderedList
                id="income-assets"
                label="Income & Balances Verification"
                value={values[incomeAndAssetVerification]}
                items={[
                  { label: "Income", id: "income" },
                  assetsVerificationItem,
                  {
                    label: "Block Applications Without Verified Income",
                    id: "block-application-submission-if-no-income",
                    disabled,
                    children: [blockApplicationSubmitIfNoIncomeItem],
                  },
                  ...conditionalItem(
                    showForceDocsInIncomeAndBalances,
                    forceDocsChildItem
                  ),
                ]}
                onChange={(checked) => {
                  const applied = onTogglePlaid(checked);
                  if (applied && checked === false) {
                    const assetsVerificationItemChildren =
                      assetsVerificationItem.children || [];
                    assetsVerificationItemChildren.forEach((child) => {
                      setFieldValue(child.id, false);
                    });
                    setFieldValue(
                      blockApplicationSubmitIfNoIncomeItem.id,
                      false
                    );
                    if (!linkEmploymentOn) {
                      setFieldValue(forceApplicantIncomeDocuments, false);
                      setFieldValue(forceOccupantIncomeDocuments, false);
                    }
                  }
                }}
                onItemChildChange={(id, value) => {
                  if (id === forceApplicantIncomeDocuments) {
                    const applied = onGuardedForceDocsToggle(
                      forceApplicantIncomeDocuments,
                      value
                    );
                    if (applied) {
                      setFieldValue(forceOccupantIncomeDocuments, value);
                    }
                    return;
                  }
                  setFieldValue(id, value);
                }}
                disabled={disabled}
              />
            </Skeleton>
          ),
        }),
        {
          key: 30,
          component: (
            <Skeleton loading={loading}>
              <CheckboxWithUnorderedList
                id="background-credit-check"
                label={
                  !isBgccEnabled ? (
                    <LabelIconContainer alignItems="center">
                      <RegularText>Background &amp; Credit check</RegularText>
                      <Tooltip
                        title={
                          <Well noBorder>
                            Background & Credit Check features are not available on your company's plan.
                            Please contact your VERO Customer Success Manager to activate this feature.
                          </Well>
                        }
                      >
                        <Icon.InfoIcon className="extra-info" />
                      </Tooltip>
                    </LabelIconContainer>
                  ) : isVeroGoActive && !values[backgroundCreditCheck] ? (
                    <LabelIconContainer alignItems="center">
                      <RegularText>Background &amp; Credit check</RegularText>
                      <Tooltip
                        title={
                          <Well noBorder>
                            Background &amp; Credit Check is turned off by default when Vero Go is active.
                            Contact your VERO Customer Success Manager to enable it.
                          </Well>
                        }
                      >
                        <Icon.InfoIcon className="extra-info" />
                      </Tooltip>
                    </LabelIconContainer>
                  ) : (
                    "Background & Credit check"
                  )
                }
                value={values[backgroundCreditCheck]}
                disabled={bgccDisabled}
                onChange={(checked) => {
                  setFieldValue(backgroundCreditCheck, checked);
                }}
                onItemChildChange={(id, value) => {
                  setFieldValue(id, value);
                }}
                items={[
                  { label: "Credit Check", id: "credit-check" },
                  {
                    label: "Eviction Related Proceedings History",
                    id: "housing-court-records-history",
                  },
                  {
                    label: "Background Check",
                    id: "background-check",
                    disabled: bgccDisabled,
                    children: [
                      getAllowManagersToViewSSNItem(
                        allowManagerViewSSN,
                        values
                      ),
                      ...conditionalItem(
                        isConditionallyProceedEnabled,
                        getConditionallyProceedItem(
                          usesConditionallyProceed,
                          values
                        )
                      ),
                    ],
                  },
                ]}
              />
            </Skeleton>
          ),
        },
        ...conditionalItem(isVerificationOfResidencyEnabled, {
          key: 35,
          component: (
            <Skeleton loading={loading}>
              <CheckboxWithUnorderedList
                id="applicant-vor"
                label={
                  <LabelIconContainer alignItems="center">
                    <RegularText>
                      Verification of Residency <WeakText>(VoR)</WeakText>
                    </RegularText>
                    <Tooltip
                      title={
                        <Well noBorder>
                          VERO will automatically send an email to each
                          applicant’s current landlord requesting them to take a
                          survey regarding their tenancy.
                        </Well>
                      }
                    >
                      <Icon.InfoIcon className="extra-info" />
                    </Tooltip>
                  </LabelIconContainer>
                }
                onChange={(checked) => toggleVor(checked, setFieldValue)}
                value={values[verificationOfResidency]}
                disabled={disabled}
              />
              {values[verificationOfResidency] && vorSurvey && (
                <VorQuestionsTable
                  isSurveyLoading={isSurveyLoading}
                  survey={vorSurvey}
                  disabled={disabled}
                  forSettingsLabel={forSettingsLabel}
                />
              )}
            </Skeleton>
          ),
        }),
        {
          key: 40,
          component: (
            <Skeleton loading={loading}>
              <CheckboxWithUnorderedList
                id="renter-profile-for-applicants"
                label="Renter Profile"
                value={values[renterProfile]}
                disabled
                items={[
                  {
                    id: "residential-history-duration",
                    label: "Residential History",
                    disabled,
                    children: [
                      {
                        id: residentialHistoryDurationFlag,
                        label: "Set Minimum History",
                        checked: values[residentialHistoryDurationFlag],
                        checkedContent: (
                          <ResidenceEmploymentDuration
                            durationTypeFieldName={
                              residentialHistoryDurationType
                            }
                            durationValueFieldName={
                              residentialHistoryDurationValue
                            }
                            disabled={disabled}
                          />
                        ),
                      },
                    ],
                  },
                  {
                    id: "employment-history-duration",
                    label: "Employment History",
                    disabled,
                    children: [
                      {
                        id: employmentHistoryDurationFlag,
                        label: "Set Minimum History",
                        checked: values[employmentHistoryDurationFlag],
                        checkedContent: (
                          <ResidenceEmploymentDuration
                            durationTypeFieldName={
                              employmentHistoryDurationType
                            }
                            durationValueFieldName={
                              employmentHistoryDurationValue
                            }
                            disabled={disabled}
                          />
                        ),
                      },
                    ],
                  },
                  {
                    id: "lease-preferences",
                    label: "Lease Preferences",
                    disabled,
                    children: [
                      {
                        id: renterProfileLeasePreferences,
                        label: "Always Require",
                        checked: values[renterProfileLeasePreferences],
                      },
                    ],
                  },
                  {
                    label: "Additional Information",
                    id: "additional-information",
                  },
                  { label: "Emergency Contacts", id: "emergency-contacts" },
                  {
                    id: "references",
                    label: "References (optional)",
                    disabled,
                    children: [
                      {
                        id: renterProfileReferences,
                        label: "Always Require",
                        checked: values[renterProfileReferences],
                      },
                    ],
                  },
                ]}
                onItemChildChange={(id, value) => {
                  if (id === residentialHistoryDurationFlag) {
                    setValues({
                      // @ts-ignore
                      ...values,
                      [residentialHistoryDurationFlag]: value,
                      [residentialHistoryDurationType]: DEFAULT_DURATION_TYPE,
                      [residentialHistoryDurationValue]: DEFAULT_DURATION_VALUE,
                    });
                  } else if (id === employmentHistoryDurationFlag) {
                    setValues({
                      // @ts-ignore
                      ...values,
                      [employmentHistoryDurationFlag]: value,
                      [employmentHistoryDurationType]: DEFAULT_DURATION_TYPE,
                      [employmentHistoryDurationValue]: DEFAULT_DURATION_VALUE,
                    });
                  } else {
                    setFieldValue(id, value);
                  }
                }}
              />
            </Skeleton>
          ),
        },
      ]}
    />
  );
};

export default ScreeningForApplicantsAndOccupants;
